import { useState, useEffect } from "react";
// react component that copies the given text inside your clipboard
import { CopyToClipboard } from "react-copy-to-clipboard";
// reactstrap components
import {
    Card,
    CardHeader,
    CardBody,
    Container,
    Row,
    Col,
    Table,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Input,
    Label,
    Badge,
} from "reactstrap";
// core components
import Header from "components/Headers/Header.js";
import rankForgeService from "services/rankForgeService";

const RankForge = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [formData, setFormData] = useState({ title: "", keywords: "", ai_provider: "openai" });
    const [submitting, setSubmitting] = useState(false);

    const toggleModal = () => setModalOpen(!modalOpen);
    const toggleDetailsModal = () => setDetailsModalOpen(!detailsModalOpen);

    const fetchArticles = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const data = await rankForgeService.getAll(token);
            setArticles(data);
        } catch (error) {
            console.error("Erro ao buscar artigos:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchArticles();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreate = async () => {
        try {
            setSubmitting(true);
            const token = localStorage.getItem("token");
            await rankForgeService.create(formData, token);
            toggleModal();
            toggleModal();
            setFormData({ title: "", keywords: "", ai_provider: "openai", backlinks: [] });
            fetchArticles(); // Refresh list
        } catch (error) {
            console.error("Erro ao criar artigo:", error);
            alert("Erro ao criar artigo. Verifique o console.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Tem certeza que deseja deletar este artigo?")) {
            try {
                const token = localStorage.getItem("token");
                await rankForgeService.delete(id, token);
                fetchArticles();
            } catch (error) {
                console.error("Erro ao deletar artigo:", error);
                alert("Erro ao deletar artigo.");
            }
        }
    };

    const handleViewDetails = (article) => {
        setSelectedArticle(article);
        toggleDetailsModal();
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case "pending":
                return <Badge color="warning">Pendente</Badge>;
            case "processing":
                return <Badge color="info">Processando</Badge>;
            case "completed":
                return <Badge color="success">Concluído</Badge>;
            case "failed":
                return <Badge color="danger">Falhou</Badge>;
            default:
                return <Badge color="secondary">{status}</Badge>;
        }
    };

    const stripHtml = (html) => {
        if (!html) return "";

        // Substituições manuais para preservar estrutura antes de limpar as tags
        let formatted = html
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/<\/p>/gi, "\n\n")
            .replace(/<\/h[1-6]>/gi, "\n\n")
            .replace(/<li[^>]*>/gi, "\n• ");

        const tmp = document.createElement("DIV");
        tmp.innerHTML = formatted;

        return (tmp.textContent || tmp.innerText || "").trim();
    };

    // Polling para atualizar o status do artigo aberto no modal
    useEffect(() => {
        let interval;
        if (detailsModalOpen && selectedArticle && ['pending', 'processing'].includes(selectedArticle.status)) {
            interval = setInterval(async () => {
                try {
                    const token = localStorage.getItem("token");
                    const updatedArticle = await rankForgeService.getById(selectedArticle.id, token);

                    // Se o status mudou para completed ou failed, atualiza lista e modal
                    if (updatedArticle.status !== selectedArticle.status) {
                        setSelectedArticle(updatedArticle);
                        // Atualiza lista geral também
                        fetchArticles();
                    }
                } catch (error) {
                    console.error("Erro ao atualizar status:", error);
                }
            }, 3000); // Checa a cada 3 segundos
        }
        return () => clearInterval(interval);
    }, [detailsModalOpen, selectedArticle]);

    // Timer de contagem regressiva visual
    const [timeLeft, setTimeLeft] = useState(60);
    useEffect(() => {
        let timer;
        if (detailsModalOpen && selectedArticle && ['pending', 'processing'].includes(selectedArticle.status)) {
            // Reinicia se abrir novo modal
            if (timeLeft === 0) setTimeLeft(60);

            timer = setInterval(() => {
                setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
            }, 1000);
        } else {
            setTimeLeft(60); // Reset
        }
        return () => clearInterval(timer);
    }, [detailsModalOpen, selectedArticle]);

    return (
        <>
            <Header />
            {/* Page content */}
            <Container className="mt--7" fluid>
                {/* ... existing content ... */}

                <Row>
                    <Col className="mb-5 mb-xl-0" xl="12">
                        <Card className="shadow">
                            <CardHeader className="border-0">
                                <Row className="align-items-center">
                                    <div className="col">
                                        <h3 className="mb-0">Artigos RankForge</h3>
                                    </div>
                                    <div className="col text-right">
                                        <Button
                                            color="primary"
                                            onClick={toggleModal}
                                            size="sm"
                                        >
                                            Novo Artigo
                                        </Button>
                                    </div>
                                </Row>
                            </CardHeader>
                            <Table className="align-items-center table-flush" responsive>
                                <thead className="thead-light">
                                    <tr>
                                        <th scope="col">Título</th>
                                        <th scope="col">Provedor</th>
                                        <th scope="col">Keywords</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Data</th>
                                        <th scope="col">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="6" className="text-center">
                                                Carregando...
                                            </td>
                                        </tr>
                                    ) : articles.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center">
                                                Nenhum artigo encontrado.
                                            </td>
                                        </tr>
                                    ) : (
                                        articles.map((article) => (
                                            <tr key={article.id}>
                                                <th scope="row">{article.title}</th>
                                                <td>
                                                    <Badge color="primary" pill>
                                                        {article.ai_provider === 'openai' ? 'ChatGPT' : 'Manus'}
                                                    </Badge>
                                                </td>
                                                <td>{article.keywords}</td>
                                                <td>{getStatusBadge(article.status)}</td>
                                                <td>{new Date(article.createdAt).toLocaleDateString()}</td>
                                                <td>
                                                    <Button
                                                        size="sm"
                                                        color="info"
                                                        onClick={() => handleViewDetails(article)}
                                                    >
                                                        Detalhes
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        color="danger"
                                                        onClick={() => handleDelete(article.id)}
                                                    >
                                                        Deletar
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Modal Novo Artigo */}
            <Modal isOpen={modalOpen} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>Novo Artigo SEO</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for="title">Título do Artigo</Label>
                            <Input
                                type="text"
                                name="title"
                                id="title"
                                placeholder="Ex: Como melhorar seu SEO"
                                value={formData.title}
                                onChange={handleInputChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="keywords">Palavras-chave (separadas por vírgula)</Label>
                            <Input
                                type="text"
                                name="keywords"
                                id="keywords"
                                placeholder="Ex: seo, marketing, google"
                                value={formData.keywords}
                                onChange={handleInputChange}
                            />
                        </FormGroup>

                        {/* Backlinks Section */}
                        <div className="mb-3">
                            <Label>Backlinks (Opcional)</Label>
                            {formData.backlinks && formData.backlinks.map((link, index) => (
                                <Row key={index} className="mb-2">
                                    <Col md="5">
                                        <Input
                                            type="text"
                                            placeholder="Texto Âncora"
                                            value={link.title}
                                            onChange={(e) => {
                                                const newBacklinks = [...(formData.backlinks || [])];
                                                newBacklinks[index].title = e.target.value;
                                                setFormData({ ...formData, backlinks: newBacklinks });
                                            }}
                                            bsSize="sm"
                                        />
                                    </Col>
                                    <Col md="5">
                                        <Input
                                            type="text"
                                            placeholder="URL (https://...)"
                                            value={link.url}
                                            onChange={(e) => {
                                                const newBacklinks = [...(formData.backlinks || [])];
                                                newBacklinks[index].url = e.target.value;
                                                setFormData({ ...formData, backlinks: newBacklinks });
                                            }}
                                            bsSize="sm"
                                        />
                                    </Col>
                                    <Col md="2">
                                        <Button
                                            color="danger"
                                            size="sm"
                                            onClick={() => {
                                                const newBacklinks = formData.backlinks.filter((_, i) => i !== index);
                                                setFormData({ ...formData, backlinks: newBacklinks });
                                            }}
                                        >
                                            <i className="ni ni-fat-remove" />
                                        </Button>
                                    </Col>
                                </Row>
                            ))}
                            <Button
                                color="secondary"
                                size="sm"
                                onClick={() => setFormData({
                                    ...formData,
                                    backlinks: [...(formData.backlinks || []), { title: '', url: '' }]
                                })}
                            >
                                + Adicionar Link
                            </Button>
                        </div>

                        <FormGroup>
                            <Label for="ai_provider">Provedor de IA</Label>
                            <Input
                                type="select"
                                name="ai_provider"
                                id="ai_provider"
                                value={formData.ai_provider}
                                onChange={handleInputChange}
                            >
                                <option value="openai">ChatGPT (OpenAI)</option>
                                <option value="manus">Manus AI</option>
                            </Input>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>
                        Cancelar
                    </Button>
                    <Button color="primary" onClick={handleCreate} disabled={submitting || !formData.title}>
                        {submitting ? "Criando..." : "Gerar Artigo"}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Modal Detalhes do Artigo */}
            <Modal isOpen={detailsModalOpen} toggle={toggleDetailsModal} size="lg">
                <ModalHeader toggle={toggleDetailsModal}>
                    {selectedArticle?.title || 'Detalhes do Artigo'}
                </ModalHeader>
                <ModalBody>
                    {selectedArticle ? (
                        <>
                            <Row className="mb-3">
                                <Col md="6">
                                    <strong>Status:</strong> {getStatusBadge(selectedArticle.status)}
                                </Col>
                                <Col md="6">
                                    <strong>Provedor:</strong> {selectedArticle.ai_provider === 'openai' ? 'ChatGPT' : 'Manus'}
                                </Col>
                            </Row>

                            {['pending', 'processing'].includes(selectedArticle.status) && (
                                <Row className="mb-4">
                                    <Col>
                                        <div className="text-center p-3 bg-secondary rounded border">
                                            <h3 className="text-muted mb-2">Gerando seu artigo...</h3>
                                            <div className="progress-wrapper">
                                                <div className="progress-info">
                                                    <div className="progress-percentage">
                                                        <span>{Math.max(0, Math.floor(((60 - timeLeft) / 60) * 100))}%</span>
                                                    </div>
                                                </div>
                                                <div className="progress">
                                                    <div
                                                        className="progress-bar bg-primary"
                                                        role="progressbar"
                                                        style={{ width: `${((60 - timeLeft) / 60) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                            <p className="mt-2 mb-0 font-weight-bold">
                                                Estimativa: {timeLeft}s restantes
                                            </p>
                                            <small className="text-muted">Aguarde, atualizando automaticamente...</small>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            <Row className="mb-3">
                                <Col>
                                    <strong>Keywords:</strong> {selectedArticle.keywords}
                                </Col>
                            </Row>
                            <hr />
                            <h5>Conteúdo Gerado:</h5>
                            <style>{`
                                .article-content {
                                    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
                                    line-height: 1.8;
                                    color: #333;
                                }
                                .article-content h1, .article-content h2, .article-content h3 {
                                    margin-top: 1.5em;
                                    margin-bottom: 0.8em;
                                    font-weight: 600;
                                    color: #111;
                                }
                                .article-content p {
                                    margin-bottom: 1.2em;
                                    text-align: justify;
                                }
                                .article-content ul, .article-content ol {
                                    margin-bottom: 1.2em;
                                    padding-left: 1.5em;
                                }
                                .article-content li {
                                    margin-bottom: 0.5em;
                                }
                                .article-content blockquote {
                                    border-left: 4px solid #ddd;
                                    padding-left: 1em;
                                    color: #666;
                                    font-style: italic;
                                }
                            `}</style>
                            <div className="p-4 bg-secondary rounded article-content" style={{ minHeight: '200px', backgroundColor: '#f8f9fe' }}>
                                {selectedArticle.content_html ? (
                                    <div dangerouslySetInnerHTML={{ __html: selectedArticle.content_html }} />
                                ) : (
                                    <p className="text-muted">Conteúdo ainda não disponível ou em processamento.</p>
                                )}
                            </div>
                        </>
                    ) : (
                        <p>Carregando...</p>
                    )}
                </ModalBody>
                <ModalFooter>
                    {selectedArticle && selectedArticle.content_html && (
                        <>
                            <CopyToClipboard
                                text={selectedArticle.content_html}
                                onCopy={() => alert("HTML copiado para a área de transferência!")}
                            >
                                <Button color="info">
                                    Copiar HTML
                                </Button>
                            </CopyToClipboard>

                            <CopyToClipboard
                                text={stripHtml(selectedArticle.content_html)}
                                onCopy={() => alert("Texto copiado para a área de transferência!")}
                            >
                                <Button color="success">
                                    Copiar Texto
                                </Button>
                            </CopyToClipboard>

                            <Button
                                color="warning"
                                onClick={async () => {
                                    if (window.confirm("Deseja regerar este artigo? O conteúdo atual será perdido.")) {
                                        try {
                                            const token = localStorage.getItem("token");
                                            await rankForgeService.regenerate(selectedArticle.id, token);
                                            // Atualiza estado local para reiniciar timer
                                            setSelectedArticle({ ...selectedArticle, status: 'pending', content_html: null });
                                            // Timer será reiniciado pelo useEffect
                                            fetchArticles();
                                        } catch (error) {
                                            console.error("Erro ao regerar:", error);
                                            alert("Erro ao regerar artigo.");
                                        }
                                    }
                                }}
                            >
                                🔄 Regerar
                            </Button>
                        </>
                    )}
                    <Button color="secondary" onClick={toggleDetailsModal}>
                        Fechar
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default RankForge;
